import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  Icons,
  Spinner,
  Surface,
  XStack,
} from '@usepepper/sprout-kit-ui';
import {
  CellContext,
  createColumnHelper,
  flexRender,
  HeaderGroup,
  Row,
} from '@tanstack/react-table';
import { COLUMN_WIDTH } from '@sales-portal/domain/pricing/constants';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { CustomColumnDef } from '@experiences/components/table/types';
import { LayoutChangeEvent, View } from 'react-native';
import { PricingDirectionalValue } from '@sales-portal/domain/pricing/screens/overview/components/pricing-directional-value';
import {
  PricingHubRecentChangeRow,
  RecentPriceChangeEvent,
} from '@sales-portal/domain/pricing/screens/overview/types';
import { PricingRecentPriceChangesTableRow } from '@sales-portal/domain/pricing/screens/overview/components/pricing-recent-price-changes-table-row';
import { Table } from '@experiences/components/table/table';
import { TableHeaderCell } from '@experiences/components/table/table-header-cell';
import { TableHeaderRow } from '@experiences/components/table/table-header-row';
import { Typography } from '@usepepper/ui/atoms/typography';

const columnHelper = createColumnHelper<PricingHubRecentChangeRow>();

// An event the backend doesn't label as a direction renders neutral.
const EVENT_BADGE: Record<
  RecentPriceChangeEvent,
  { color: 'error' | 'muted' | 'success'; label: () => string }
> = {
  decrease: {
    color: 'error',
    label: () => i18n.t('pricing.overview.event.decrease'),
  },
  increase: {
    color: 'success',
    label: () => i18n.t('pricing.overview.event.increase'),
  },
  unknown: { color: 'muted', label: () => '--' },
};

const ROW_HEIGHT = 56;
const ESTIMATED_HEADER_HEIGHT = 40;
const MIN_TABLE_HEIGHT = 420;

interface Props {
  hasError?: boolean;
  isLoading?: boolean;
  onRetry?: () => void;
  onRowPress?: (row: PricingHubRecentChangeRow) => void;
  onViewAll?: () => void;
  rows: PricingHubRecentChangeRow[];
}

export const PricingRecentPriceChangesTable: React.FC<Props> = ({
  hasError = false,
  isLoading = false,
  onRetry,
  onRowPress,
  onViewAll,
  rows,
}) => {
  const theme = useTheme();
  const [styles] = useStyles();
  const [layoutWidth, setLayoutWidth] = useState<number | null>(null);

  const handleLayout = (event: LayoutChangeEvent) => {
    setLayoutWidth(event.nativeEvent.layout.width);
  };

  const columns = useMemo(() => {
    const columnDefs: {
      cell: (
        info: CellContext<PricingHubRecentChangeRow, unknown>,
      ) => React.ReactNode;
      header: () => React.ReactNode;
      id: string;
      maxSize?: number;
      minSize?: number;
    }[] = [
      {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.secondary}
            variant="paragraph-md"
          >
            {info.row.original.date}
          </Typography>
        ),
        header: () => i18n.t('pricing.overview.table.header.date'),
        id: 'date',
        minSize: COLUMN_WIDTH.S.minSize,
      },
      {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.secondary}
            variant="paragraph-md"
          >
            {info.row.original.source}
          </Typography>
        ),
        header: () => i18n.t('pricing.overview.table.header.source'),
        id: 'source',
        minSize: COLUMN_WIDTH.M.minSize,
      },
      {
        cell: (info) => {
          const event = EVENT_BADGE[info.row.original.event];
          return <Badge color={event.color}>{event.label()}</Badge>;
        },
        header: () => i18n.t('pricing.overview.table.header.event'),
        id: 'event',
        minSize: COLUMN_WIDTH.S.minSize,
      },
      {
        cell: (info) => (
          <Typography numberOfLines={1} variant="paragraph-md">
            {info.row.original.customer}
          </Typography>
        ),
        header: () => i18n.t('pricing.table.header.customer'),
        id: 'customer',
        minSize: COLUMN_WIDTH.M.minSize,
      },
      {
        cell: (info) => (
          <Typography numberOfLines={1} variant="paragraph-md">
            {info.row.original.item}
          </Typography>
        ),
        header: () => i18n.t('pricing.overview.table.header.item'),
        id: 'item',
        minSize: COLUMN_WIDTH.M.minSize,
      },
      {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.secondary}
            textTransform="capitalize"
            variant="paragraph-md"
          >
            {info.row.original.uom}
          </Typography>
        ),
        header: () => i18n.t('pricing.table.header.uom'),
        id: 'uom',
        minSize: COLUMN_WIDTH.XS.minSize,
      },
      {
        cell: (info) => (
          <PricingDirectionalValue
            direction={info.row.original.priceDirection}
            text={info.row.original.price}
            variant="paragraph-md-600"
          />
        ),
        header: () => i18n.t('pricing.table.header.price'),
        id: 'price',
        minSize: COLUMN_WIDTH.S.minSize,
      },
      {
        cell: (info) => (
          <Typography variant="paragraph-md">
            {info.row.original.cost}
          </Typography>
        ),
        header: () => i18n.t('pricing.overview.table.header.cost'),
        id: 'cost',
        minSize: COLUMN_WIDTH.S.minSize,
      },
      {
        cell: (info) => (
          <PricingDirectionalValue
            direction={info.row.original.marginDirection}
            text={info.row.original.margin}
          />
        ),
        header: () => i18n.t('pricing.overview.table.header.margin'),
        id: 'margin',
        minSize: COLUMN_WIDTH.S.minSize,
      },
      {
        cell: (info) => (
          <PricingDirectionalValue
            direction={info.row.original.gpPerWeekDirection}
            text={info.row.original.gpPerWeek}
          />
        ),
        header: () =>
          i18n.t('pricing.overview.table.header.gross_profit_per_week'),
        id: 'gp',
        minSize: COLUMN_WIDTH.S.minSize,
      },
    ];

    const initialPercentWidth = 100 / columnDefs.length;

    return columnDefs.map(({ cell, header, id, maxSize, minSize }) =>
      columnHelper.display({
        cell: (info) => cell(info),
        header,
        id,
        maxSize,
        meta: { initialPercentWidth },
        minSize,
      }),
    ) as CustomColumnDef<PricingHubRecentChangeRow>[];
  }, [theme]);

  const renderHeaderRow = useCallback(
    ({
      headerGroup,
    }: {
      headerGroup: HeaderGroup<PricingHubRecentChangeRow>;
    }) => (
      <TableHeaderRow key={headerGroup.id}>
        {headerGroup.headers.map((header) => (
          <TableHeaderCell columnId={header.id} key={header.id}>
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-sm-600"
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
            </Typography>
          </TableHeaderCell>
        ))}
      </TableHeaderRow>
    ),
    [theme],
  );

  const renderRow = useCallback(
    ({
      index,
      row,
    }: {
      index: number;
      row: Row<PricingHubRecentChangeRow>;
    }) => (
      <PricingRecentPriceChangesTableRow
        index={index}
        key={row.id}
        onRowPress={onRowPress}
        row={row}
      />
    ),
    [onRowPress],
  );

  const isEmpty = hasError || rows.length === 0;

  return (
    <Surface>
      <XStack
        alignItems="center"
        borderBottomWidth={1}
        borderColor={theme.sk.color.border.primary}
        justifyContent="space-between"
        paddingHorizontal="$16"
        paddingVertical="$16"
      >
        <Typography variant="heading-sm">
          {i18n.t('pricing.overview.recent_changes.title')}
        </Typography>
        {onViewAll ? (
          <Button
            accessibilityLabel={i18n.t(
              'pricing.overview.recent_changes.view_all_a11y',
            )}
            iconAfter={Icons.ChevronRight}
            onPress={onViewAll}
            size="$sm"
            variant="tertiary"
          >
            {i18n.t('pricing.overview.recent_changes.view_all')}
          </Button>
        ) : null}
      </XStack>
      <View onLayout={handleLayout} style={styles.body}>
        {isLoading ? (
          <View style={styles.centered}>
            <Spinner size="large" />
          </View>
        ) : isEmpty ? (
          <View style={styles.centered}>
            <EmptyState
              action={
                hasError && onRetry ? (
                  <Button
                    accessibilityLabel={i18n.t('sales_portal.error.retry')}
                    onPress={onRetry}
                    size="$sm"
                    variant="secondary"
                  >
                    {i18n.t('sales_portal.error.retry')}
                  </Button>
                ) : undefined
              }
              description={i18n.t(
                hasError
                  ? 'pricing.error.table.description'
                  : 'pricing.overview.recent_changes.empty.description',
              )}
              icon={
                hasError ? (
                  <Icons.AlertCircle
                    color={theme.sk.color.icon.negative.primary}
                    size={24}
                  />
                ) : (
                  <Icons.Activity
                    color={theme.sk.color.icon.secondary}
                    size={24}
                  />
                )
              }
              title={i18n.t(
                hasError
                  ? 'pricing.error.table.title'
                  : 'pricing.overview.recent_changes.empty.title',
              )}
              tone={hasError ? 'error' : 'neutral'}
            />
          </View>
        ) : layoutWidth ? (
          <Table
            cellPadding={theme.sk.spacing.spaceSmall}
            columns={columns}
            data={rows}
            emptyComponent={null}
            estimatedHeaderHeight={ESTIMATED_HEADER_HEIGHT}
            estimatedRowHeight={ROW_HEIGHT}
            headerPadding={theme.sk.spacing.spaceSmall}
            isActive
            layoutWidth={layoutWidth}
            renderHeaderRow={renderHeaderRow}
            renderRow={renderRow}
          />
        ) : (
          <View
            accessibilityRole="progressbar"
            accessibilityState={{ busy: true }}
            accessible
            onLayout={handleLayout}
            style={styles.body}
          />
        )}
      </View>
    </Surface>
  );
};

const useStyles = createStyles((theme) => ({
  body: {
    minHeight: MIN_TABLE_HEIGHT,
  },
  centered: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: theme.sk.spacing.spaceLarge,
  },
}));
